import {
  getDtesByReceiptId,
  generateDteForReceipt,
  DteRecord,
} from '../../../../../../../../crud/payments_api/dte';

export const BOLETA_TIPO_DTE = 39;
const BOLETA_INDICADOR_SERVICIO = 3;

export interface BoletaPrintTarget {
  dteId: number;
  folio: number;
  tipoDte: number;
  localId: number;
}

/** A tipo-39 record is reusable (reprintable) if it has a folio and isn't failed/rejected. */
const isReusableBoleta = (dte: DteRecord): boolean =>
  dte.tipo_dte === BOLETA_TIPO_DTE &&
  dte.folio !== null &&
  !dte.failed &&
  !dte.sii_rejected;

/** Pick the best existing boleta: prefer SII-accepted, then the most recently created one. */
const pickBestBoleta = (dtes: DteRecord[]): DteRecord | undefined => {
  const candidates = dtes.filter(isReusableBoleta);
  if (candidates.length === 0) return undefined;
  return candidates.sort((a, b) => {
    if (a.sii_accepted !== b.sii_accepted) return a.sii_accepted ? -1 : 1;
    return (
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime() ||
      b.dte_id - a.dte_id
    );
  })[0];
};

/** Extracts a human-readable message from a backend DTE error response. */
const parseBoletaError = (error: any): string => {
  const data = error?.response?.data;
  if (!data) return 'No fue posible generar la boleta';
  let message = data.error || data.message || 'No fue posible generar la boleta';
  const details = data.details;
  if (details) {
    if (typeof details === 'string') {
      message = details;
    } else if (details.message) {
      message = details.message;
      if (details.details) {
        try {
          const nested = JSON.parse(details.details);
          if (nested.mensaje) message = nested.mensaje;
        } catch {
          message = details.details;
        }
      }
    }
  }
  return message;
};

/**
 * Returns a printable SII Boleta (tipo 39) for a receipt in a single step.
 *
 * Flow (duplicate-safe):
 *  1. Reuse an existing reprintable tipo-39 boleta if one exists.
 *  2. Otherwise generate a new boleta (anonymous receptor, indicador_servicio=3).
 *  3. If the backend reports an already-accepted boleta (HTTP 409), reprint that one.
 *
 * Throws an Error with a friendly message on real failures (caller should toast it).
 */
export const printBoletaForReceipt = async (
  localId: number,
  receiptId: number
): Promise<BoletaPrintTarget> => {
  // 1. Reuse an existing boleta if available.
  try {
    const response = await getDtesByReceiptId(localId, receiptId, false);
    if (response.data?.success) {
      const existing = pickBestBoleta(response.data.dtes || []);
      if (existing) {
        return {
          dteId: existing.dte_id,
          folio: existing.folio as number,
          tipoDte: BOLETA_TIPO_DTE,
          localId,
        };
      }
    }
  } catch {
    // Non-fatal: fall through to generation. Backend still dedups accepted boletas via 409.
  }

  // 2. Generate a new boleta.
  try {
    const response = await generateDteForReceipt(localId, receiptId, {
      dte_type: BOLETA_TIPO_DTE,
      indicador_servicio: BOLETA_INDICADOR_SERVICIO,
    });
    return {
      dteId: response.data.dte_record_id,
      folio: response.data.folio,
      tipoDte: BOLETA_TIPO_DTE,
      localId,
    };
  } catch (error: any) {
    // 3. An accepted boleta already exists -> reprint it.
    const existingDte = error?.response?.data?.existing_dte;
    if (error?.response?.status === 409 && existingDte) {
      return {
        dteId: existingDte.dte_id,
        folio: existingDte.folio,
        tipoDte: BOLETA_TIPO_DTE,
        localId,
      };
    }
    throw new Error(parseBoletaError(error));
  }
};

export default printBoletaForReceipt;
